import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, Button, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import * as api from '../../api';
import Header from '../../components/Header';

export default function ClubManage({ locale }) {
  const { clubId } = useParams();
  const { t } = useTranslation();
  const [club, setClub] = useState(null);
  const [shooters, setShooters] = useState([]);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newShooter, setNewShooter] = useState({ publicId: '', name: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    api.getClub(clubId).then(r => setClub(r.data)),
    api.getClubShooters(clubId).then(r => setShooters(r.data || [])),
    api.getClubAuth(clubId).then(r => setAuth(r.data)).catch(() => null),
  ]).catch(e => setError(e.message)).finally(() => setLoading(false));

  useEffect(() => { load(); }, [clubId]);

  const handleAddShooter = async () => {
    setSaving(true);
    try {
      await api.addClubShooter(clubId, { ...newShooter, registrationEndDate: new Date().toISOString() });
      await load();
      setShowAdd(false);
      setNewShooter({ publicId: '', name: '', phone: '', email: '' });
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteShooter = async (shooterId) => {
    if (!confirm(t('confirmDelete'))) return;
    try {
      await api.deleteClubShooter(clubId, shooterId);
      setShooters(s => s.filter(sh => sh.id !== shooterId));
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <>
      <Header locale={locale} />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>{club?.title || clubId}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('shooters')} ({shooters.length})</Typography>
          {auth && <Button startIcon={<AddIcon />} variant="contained" onClick={() => setShowAdd(true)}>{t('add')}</Button>}
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('publicId')}</TableCell>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('email')}</TableCell>
              <TableCell>{t('phone')}</TableCell>
              {auth && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {shooters.map(s => (
              <TableRow key={s.id}>
                <TableCell>{s.publicId}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>{s.phone}</TableCell>
                {auth && (
                  <TableCell>
                    <IconButton size="small" onClick={() => handleDeleteShooter(s.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={showAdd} onClose={() => setShowAdd(false)}>
          <DialogTitle>{t('addShooter')}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
            <TextField label={t('publicId')} value={newShooter.publicId} onChange={e => setNewShooter(p => ({ ...p, publicId: e.target.value }))} />
            <TextField label={t('name')} value={newShooter.name} onChange={e => setNewShooter(p => ({ ...p, name: e.target.value }))} />
            <TextField label={t('email')} value={newShooter.email} onChange={e => setNewShooter(p => ({ ...p, email: e.target.value }))} />
            <TextField label={t('phone')} value={newShooter.phone} onChange={e => setNewShooter(p => ({ ...p, phone: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>{t('cancel')}</Button>
            <Button variant="contained" onClick={handleAddShooter} disabled={saving}>{saving ? <CircularProgress size={20} /> : t('save')}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
